import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { HelpOutline, Check, CloudUpload, Edit } from '@mui/icons-material';
import { api } from '../../api/apiService';
import { countries } from '../../constants';
import { imageService } from '../../api/imageService';
import { CompanyLocationMap } from './CompanyLocationMap';
import { EmbeddedFormSaveButton } from '../forms/EmbeddedFormSaveButton';

const DESCRIPTION_HINT =
  "Describe your company's mission, focus, and what makes it unique in the news industry. " +
  "Example: A financial news company specializing in real-time stock market analysis and clear reporting for investors.";

const CompanyDescriptionForm: React.FC = () => {
  const [companyName, setCompanyName] = useState<string>('');
  const [editingName, setEditingName] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [location, setLocation] = useState<string | null>(null);
  const [newValue, setNewValue] = useState<string>('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brandVoice, setBrandVoice] = useState('');
  const [learningVoice, setLearningVoice] = useState(false);
  const [voiceNote, setVoiceNote] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({
    companyName: '',
    description: '',
    location: '',
  });

  const MAX_CORE_VALUES = 10;
  const MAX_DESCRIPTION_WORDS = 50;

  useEffect(() => {
    api
      .get('/api/config/company-profile')
      .then((response: Record<string, unknown>) => {
        setCompanyName(String(response.company_name || ''));
        setDescription(String(response.description || ''));
        setCoreValues((response.core_values as string[]) || []);
        setLocation((response.location as string | null) || null);
        setBrandVoice(String(response.brand_voice || ''));
        const logo =
          (response.company_logo_url as string) ||
          (response.logo_url as string) ||
          (response.company_logo as string) ||
          '';
        setCompanyLogoUrl(typeof logo === 'string' ? logo : '');
      })
      .catch((err) => {
        console.error('Failed to fetch company profile:', err);
      });
  }, []);

  const handleDescriptionChange = (newDescription: string) => {
    const wordCount = newDescription.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount <= MAX_DESCRIPTION_WORDS) {
      setDescription(newDescription);
    }
  };

  const handleAddValue = () => {
    if (newValue.trim() && !coreValues.includes(newValue.trim()) && coreValues.length < MAX_CORE_VALUES) {
      setCoreValues([...coreValues, newValue.trim()]);
      setNewValue('');
    }
  };

  const handleDeleteValue = (value: string) => {
    setCoreValues(coreValues.filter((v) => v !== value));
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setLogoUploading(true);
    setSaveMessage(null);
    try {
      const res = await imageService.ingestOgImage({ file });
      if (res?.url) setCompanyLogoUrl(res.url);
      else setSaveMessage({ type: 'error', text: 'Upload did not return a URL.' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Logo upload failed. Try a smaller image or different format.' });
    } finally {
      setLogoUploading(false);
    }
  };

  const validateForm = (): boolean => {
    const formErrors: { [key: string]: string } = {};
    let isValid = true;

    if (!companyName.trim()) {
      formErrors.companyName = 'Company name is required.';
      isValid = false;
    } else {
      formErrors.companyName = '';
    }

    if (!description.trim()) {
      formErrors.description = 'Company description is required.';
      isValid = false;
    } else {
      formErrors.description = '';
    }

    if (!location) {
      formErrors.location = 'Location is required.';
      isValid = false;
    } else {
      formErrors.location = '';
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMessage(null);
    if (!validateForm()) return;

    const payload: Record<string, unknown> = {
      company_name: companyName,
      description,
      core_values: coreValues,
      location,
    };
    if (companyLogoUrl.trim()) {
      payload.company_logo_url = companyLogoUrl.trim();
    }

    setIsSubmitting(true);
    try {
      await api.put('/api/config/company-profile', payload);
      setSaveMessage({ type: 'success', text: 'Company profile saved.' });
    } catch (err) {
      console.error(err);
      setSaveMessage({ type: 'error', text: 'Could not save company profile. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const wordCount = description.trim().split(/\s+/).filter(Boolean).length;

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fafafa',
      '& fieldset': { borderColor: '#3f3f46' },
      '&:hover fieldset': { borderColor: '#a1a1aa' },
      '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
    },
    '& .MuiInputLabel-root': { color: '#a1a1aa' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
    '& .MuiFormHelperText-root': { color: '#a1a1aa' },
    '& .MuiFormHelperText-root.Mui-error': { color: '#f87171' },
  };

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 'none',
        mx: 0,
        mt: 0,
        p: { xs: 1, sm: 2 },
        boxShadow: 'none',
        borderRadius: 2,
        bgcolor: '#0a0a0a',
        border: '1px solid #27272a',
      }}
    >
      <CardContent sx={{ bgcolor: '#0a0a0a', maxWidth: 'none', width: '100%', px: { xs: 0, sm: 1 } }}>
        {saveMessage && (
          <Alert
            severity={saveMessage.type}
            onClose={() => setSaveMessage(null)}
            sx={{ mb: 2, border: '1px solid #3f3f46' }}
          >
            {saveMessage.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: errors.companyName ? 0 : 2, flexWrap: 'wrap' }}>
          {editingName ? (
            <TextField
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              autoFocus
              variant="standard"
              placeholder="Company name"
              error={!!errors.companyName}
              sx={{
                flex: 1,
                minWidth: 200,
                '& .MuiInput-root': { fontSize: '1.5rem', fontWeight: 600, color: '#fafafa' },
                '& .MuiInput-underline:before': { borderColor: '#3f3f46' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderColor: '#a1a1aa' },
                '& .MuiInput-underline:after': { borderColor: '#60a5fa' },
              }}
            />
          ) : (
            <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 600, flex: 1, minWidth: 0 }}>
              {companyName.trim() || 'Company name'}
            </Typography>
          )}
          {!editingName ? (
            <Tooltip title="Edit company name">
              <IconButton
                aria-label="Edit company name"
                onClick={() => setEditingName(true)}
                sx={{ color: '#a1a1aa', '&:hover': { color: '#fafafa' } }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Done">
              <IconButton
                aria-label="Finish editing company name"
                onClick={() => setEditingName(false)}
                sx={{ color: '#86efac', '&:hover': { color: '#bbf7d0' } }}
              >
                <Check />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {errors.companyName ? (
          <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mb: 2 }}>
            {errors.companyName}
          </Typography>
        ) : null}

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleLogoFile} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            gap: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1,
              width: { xs: '100%', sm: 128 },
              maxWidth: { xs: 200, sm: 140 },
              alignSelf: { xs: 'center', sm: 'stretch' },
            }}
          >
            <Box
              component="button"
              type="button"
              disabled={logoUploading}
              aria-label="Upload company logo"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: '100%',
                minHeight: 96,
                flex: { xs: 'none', sm: 1 },
                borderRadius: 2,
                border: '1px solid #3f3f46',
                overflow: 'hidden',
                bgcolor: '#111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: logoUploading ? 'wait' : 'pointer',
                p: 0,
                font: 'inherit',
                '&:hover': { borderColor: '#60a5fa', bgcolor: '#141414' },
                '&:disabled': { opacity: 0.7 },
              }}
            >
              {logoUploading ? (
                <CircularProgress size={32} sx={{ color: '#60a5fa' }} />
              ) : companyLogoUrl ? (
                <Box
                  component="img"
                  src={companyLogoUrl}
                  alt="Company logo"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <CloudUpload sx={{ fontSize: 40, color: '#52525b' }} />
              )}
            </Box>
            {companyLogoUrl ? (
              <Button size="small" onClick={() => setCompanyLogoUrl('')} sx={{ color: '#a1a1aa' }}>
                Remove logo
              </Button>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
                {`Core values (${coreValues.length}/${MAX_CORE_VALUES})`}
              </Typography>
              <Tooltip
                title={
                  <>
                    Add principles that guide your company.
                    <br />
                    <br />
                    Example: Transparency, Accuracy, Investor-focused
                  </>
                }
              >
                <IconButton size="small" sx={{ color: '#a1a1aa' }} aria-label="Core values help">
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {coreValues.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onDelete={() => handleDeleteValue(value)}
                  sx={{
                    bgcolor: '#27272a',
                    color: '#fafafa',
                    border: '1px solid #3f3f46',
                    '& .MuiChip-deleteIcon': {
                      color: '#a1a1aa',
                      '&:hover': { color: '#f87171' },
                    },
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Add core value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                size="small"
                disabled={coreValues.length >= MAX_CORE_VALUES}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddValue();
                  }
                }}
                sx={{ ...textFieldSx, flex: 1, minWidth: 180, maxWidth: 480 }}
              />
              <Tooltip title="Add value">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleAddValue}
                    disabled={
                      coreValues.length >= MAX_CORE_VALUES || !newValue.trim() || coreValues.includes(newValue.trim())
                    }
                    sx={{
                      bgcolor: '#27272a',
                      border: '1px solid #3f3f46',
                      color: '#86efac',
                      '&:hover': { bgcolor: '#3f3f46' },
                      '&.Mui-disabled': { color: '#52525b', borderColor: '#27272a' },
                    }}
                  >
                    <Check />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Company Description"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            margin="normal"
            error={!!errors.description}
            helperText={errors.description || undefined}
            inputProps={{ maxLength: 500 }}
            required
            sx={{ ...textFieldSx, width: '100%', display: 'block' }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#71717a', lineHeight: 1.5 }}>
            {DESCRIPTION_HINT}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#a1a1aa' }}>
            {`Description length: ${wordCount} / ${MAX_DESCRIPTION_WORDS} words`}
          </Typography>

          <Autocomplete
            options={countries}
            value={location}
            onChange={(_, newValue) => setLocation(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                margin="normal"
                fullWidth
                error={!!errors.location}
                helperText={errors.location}
                sx={textFieldSx}
              />
            )}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: '#0a0a0a',
                  border: '1px solid #27272a',
                  '& .MuiAutocomplete-option': {
                    color: '#fafafa',
                    '&:hover': { bgcolor: '#1a1a1a' },
                    '&[aria-selected="true"]': { bgcolor: '#27272a' },
                    '&.Mui-focused': { bgcolor: '#27272a' },
                  },
                },
              },
            }}
          />

          <Box sx={{ mt: 3 }}>
            <CompanyLocationMap query={location} />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
              Brand voice
            </Typography>
            <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5, mb: 1.5 }}>
              Learned once from the radar coverage tracked for this company. Every article uses it.
            </Typography>
            {brandVoice ? (
              <Typography variant="body2" sx={{ color: '#e4e4e7', mb: 1.5, whiteSpace: 'pre-wrap' }}>
                {brandVoice}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#71717a', mb: 1.5 }}>
                Not learned yet.
              </Typography>
            )}
            <Button
              variant="outlined"
              disabled={learningVoice}
              onClick={() => {
                setLearningVoice(true);
                setVoiceNote('');
                api
                  .post('/api/config/company-profile/learn-brand-voice', {})
                  .then((response: Record<string, unknown>) => {
                    setBrandVoice(String(response.brand_voice || ''));
                    const read = Number(response.items_read || 0);
                    setVoiceNote(
                      read
                        ? `Learned from ${read} radar items.`
                        : 'Learned from the radar coverage for this company.',
                    );
                  })
                  .catch((err: { response?: { data?: unknown }; message?: string }) => {
                    const data = err?.response?.data;
                    setVoiceNote(
                      typeof data === 'string' && data.trim()
                        ? data
                        : err?.message || 'Could not learn a brand voice from radar coverage.',
                    );
                  })
                  .finally(() => setLearningVoice(false));
              }}
            >
              {learningVoice ? 'Learning from radar…' : 'Learn from radar'}
            </Button>
            {voiceNote && (
              <Typography variant="caption" sx={{ display: 'block', color: '#a1a1aa', mt: 1 }}>
                {voiceNote}
              </Typography>
            )}
          </Box>

          <EmbeddedFormSaveButton loading={isSubmitting} idleTooltip="Save company profile" loadingTooltip="Saving…" />
        </form>
      </CardContent>
    </Card>
  );
};

export default CompanyDescriptionForm;
